import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, euro, formatApiError } from "../lib/api";
import { C, field, btnPrimary, softShadow } from "../lib/theme";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon, Clock, Users, User, Phone, Mail, StickyNote,
  ChevronLeft, ChevronRight, Check, Send, MapPin, CheckCircle2,
} from "lucide-react";

const STEPS = ["Data", "Orario", "Persone", "Dettagli", "Riepilogo"];

function nextDays(n) {
  const out = [];
  const today = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    out.push(d);
  }
  return out;
}
const iso = (d) => d.toISOString().slice(0, 10);
const dayLabel = (d) => d.toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" });

export default function Booking() {
  const { restaurantId } = useParams();
  const nav = useNavigate();

  const [restaurant, setRestaurant] = useState(null);
  const [step, setStep] = useState(0);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState(null);
  const [party, setParty] = useState(2);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get(`/restaurants/${restaurantId}`).then((r) => { setRestaurant(r.data); if (r.data.min_party_size) setParty(r.data.min_party_size); }).catch(() => {
      toast.error("Ristorante non trovato");
      nav("/");
    });
  }, [restaurantId]);

  useEffect(() => {
    if (!date) return;
    setTime(null);
    api.get(`/restaurants/${restaurantId}/availability`, { params: { date: iso(date) } })
      .then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, [date, restaurantId]);

  if (!restaurant) return <div className="min-h-[60vh] flex items-center justify-center font-semibold" style={{ color: C.CORAL }}>Caricamento…</div>;

  const estimate = (restaurant.deposit_per_person || 0) * party;
  const accent = restaurant.accent_color || C.CORAL;
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const canNext = () => {
    if (step === 0) return !!date;
    if (step === 1) return !!time;
    if (step === 2) return party >= (restaurant?.min_party_size || 1);
    if (step === 3) return name.trim() && phone.trim() && emailOk;
    return true;
  };

  const submitRequest = async () => {
    setSending(true);
    try {
      await api.post("/reservations", {
        restaurant_id: restaurantId, date: iso(date), time, party_size: party,
        customer_name: name.trim(), customer_phone: phone.trim(), customer_email: email.trim(), notes,
      });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-5 py-12">
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-3xl p-10 max-w-md w-full text-center" style={softShadow} data-testid="request-sent">
          <CheckCircle2 className="w-16 h-16 mx-auto mb-5" style={{ color: C.GREEN }} />
          <h1 className="font-display text-3xl font-extrabold mb-2" style={{ color: C.INK }}>Richiesta inviata!</h1>
          <p className="mb-8" style={{ color: "#6b665d" }}>Il nostro staff ti contatterà per la conferma e l'invio del <b style={{ color: accent }}>link di pagamento della caparra</b>.</p>
          <Link to="/" className={`${btnPrimary} inline-flex`} style={{ background: accent }}>Torna alla home</Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
      <div className="flex items-center gap-4 mb-8">
        {restaurant.logo_image || restaurant.cover_image ? (
          <img src={restaurant.logo_image || restaurant.cover_image} alt="" className="w-16 h-16 rounded-2xl object-cover" />
        ) : (
          <span className="w-16 h-16 rounded-2xl flex items-center justify-center font-display text-2xl font-black text-white" style={{ background: accent }}>{restaurant.name?.[0] || "R"}</span>
        )}
        <div>
          <h1 className="font-display text-3xl font-extrabold" style={{ color: C.INK }}>{restaurant.name}</h1>
          {restaurant.address ? <p className="text-sm flex items-center gap-1.5" style={{ color: C.MUTED }}><MapPin className="w-3.5 h-3.5" style={{ color: accent }} /> {restaurant.address}</p> : null}
        </div>
      </div>

      <div className="mb-6 rounded-2xl p-4 text-sm" style={{ background: "#FEEDE7", color: "#5c4a44" }} data-testid="group-notice">
        Le prenotazioni online sono riservate ai <b>gruppi da {restaurant.min_party_size} persone in su</b> (con caparra). Per tavoli più piccoli contatta direttamente il ristorante{restaurant.phone ? <> al <b>{restaurant.phone}</b></> : null}{restaurant.whatsapp ? <> o su WhatsApp <b>{restaurant.whatsapp}</b></> : null}.
      </div>

      <div className="flex items-center justify-between mb-10">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-colors" style={
                i < step ? { background: C.GREEN, color: "#fff" } : i === step ? { background: accent, color: "#fff", boxShadow: `0 0 0 4px ${accent}2e` } : { background: "#fff", color: "#b7ad99", border: `1px solid ${C.BORDER}` }
              }>{i < step ? <Check className="w-4 h-4" /> : i + 1}</div>
              <span className="text-xs mt-2 font-semibold" style={{ color: i <= step ? accent : "#b7ad99" }}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className="h-0.5 flex-1 mx-2" style={{ background: i < step ? C.GREEN : "#eadfca" }} />}
          </div>
        ))}
      </div>

      <div className="bg-white rounded-3xl p-6 sm:p-8 min-h-[320px]" style={softShadow}>
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
            {step === 0 && (
              <div>
                <h2 className="font-display text-2xl font-extrabold mb-1 flex items-center gap-2" style={{ color: C.INK }}><CalendarIcon className="w-5 h-5" style={{ color: accent }} /> Scegli la data</h2>
                <p className="text-sm mb-6" style={{ color: C.MUTED }}>Seleziona il giorno della prenotazione.</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {nextDays(14).map((d) => {
                    const active = date && iso(d) === iso(date);
                    return <button key={iso(d)} data-testid={`date-${iso(d)}`} onClick={() => setDate(d)}
                      className="py-3 rounded-xl text-sm font-semibold border transition-colors" style={active ? { background: accent, color: "#fff", borderColor: accent } : { background: "#fff", color: "#4a453f", borderColor: C.BORDER }}>{dayLabel(d)}</button>;
                  })}
                </div>
              </div>
            )}
            {step === 1 && (
              <div>
                <h2 className="font-display text-2xl font-extrabold mb-1 flex items-center gap-2" style={{ color: C.INK }}><Clock className="w-5 h-5" style={{ color: accent }} /> Scegli l'orario</h2>
                <p className="text-sm mb-6" style={{ color: C.MUTED }}>Fasce disponibili per {date && dayLabel(date)}.</p>
                <select data-testid="slot-select" value={time || ""} onChange={(e) => setTime(e.target.value)} className={field}>
                  <option value="" disabled>Seleziona un orario</option>
                  {slots.map((s) => (<option key={s.time} value={s.time} disabled={!s.available}>{s.time}{!s.available ? " (esaurito)" : ""}</option>))}
                </select>
                {slots.length === 0 && <p className="text-sm mt-3" style={{ color: C.MUTED }}>Nessuna fascia disponibile per questa data.</p>}
              </div>
            )}
            {step === 2 && (
              <div>
                <h2 className="font-display text-2xl font-extrabold mb-1 flex items-center gap-2" style={{ color: C.INK }}><Users className="w-5 h-5" style={{ color: accent }} /> Quante persone?</h2>
                <p className="text-sm mb-6" style={{ color: C.MUTED }}>Da {restaurant.min_party_size} a {restaurant.max_party_size} persone.</p>
                <div className="flex items-center justify-center gap-8 py-6">
                  <button data-testid="party-minus" onClick={() => setParty(Math.max(restaurant.min_party_size || 1, party - 1))} className="w-14 h-14 rounded-full bg-white border text-2xl font-bold transition-colors hover:border-[#FF5A36]" style={{ borderColor: C.BORDER, color: C.INK }}>−</button>
                  <span data-testid="party-count" className="font-display text-6xl font-black w-20 text-center" style={{ color: accent }}>{party}</span>
                  <button data-testid="party-plus" onClick={() => setParty(Math.min(restaurant.max_party_size, party + 1))} className="w-14 h-14 rounded-full bg-white border text-2xl font-bold transition-colors hover:border-[#FF5A36]" style={{ borderColor: C.BORDER, color: C.INK }}>+</button>
                </div>
                <p className="text-center text-sm" style={{ color: C.MUTED }}>Caparra indicativa: <span className="font-bold" style={{ color: C.GREEN }}>{euro(estimate)}</span> (la confermerà il ristorante)</p>
              </div>
            )}
            {step === 3 && (
              <div className="space-y-5">
                <h2 className="font-display text-2xl font-extrabold mb-1 flex items-center gap-2" style={{ color: C.INK }}><User className="w-5 h-5" style={{ color: accent }} /> I tuoi dati</h2>
                <p className="text-sm" style={{ color: C.MUTED }}>Non serve registrarsi: invii una richiesta come ospite. Telefono ed email sono obbligatori per ricevere la conferma e il link di pagamento.</p>
                <div><label className="block text-sm font-semibold mb-2" style={{ color: "#4a453f" }}>Nome e cognome *</label>
                  <input data-testid="booking-name" value={name} onChange={(e) => setName(e.target.value)} className={field} placeholder="Mario Rossi" /></div>
                <div><label className="block text-sm font-semibold mb-2 flex items-center gap-1.5" style={{ color: "#4a453f" }}><Phone className="w-4 h-4" /> Telefono *</label>
                  <input data-testid="booking-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className={field} placeholder="+39 333 1234567" /></div>
                <div><label className="block text-sm font-semibold mb-2 flex items-center gap-1.5" style={{ color: "#4a453f" }}><Mail className="w-4 h-4" /> Email *</label>
                  <input data-testid="booking-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} placeholder="tu@email.com" /></div>
                <div><label className="block text-sm font-semibold mb-2 flex items-center gap-1.5" style={{ color: "#4a453f" }}><StickyNote className="w-4 h-4" /> Note (facoltative)</label>
                  <textarea data-testid="booking-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={`${field} resize-none`} placeholder="Allergie, occasioni speciali…" /></div>
              </div>
            )}
            {step === 4 && (
              <div data-testid="booking-recap">
                <h2 className="font-display text-2xl font-extrabold mb-1 flex items-center gap-2" style={{ color: C.INK }}><Check className="w-5 h-5" style={{ color: accent }} /> Riepilogo richiesta</h2>
                <p className="text-sm mb-6" style={{ color: C.MUTED }}>Controlla i dettagli prima di inviare la richiesta.</p>
                <div className="rounded-2xl border overflow-hidden" style={{ borderColor: C.BORDER }}>
                  {[["Ristorante", restaurant.name],
                    ["Data", date && date.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })],
                    ["Orario", time], ["Persone", `${party}`], ["A nome di", name], ["Telefono", phone], ["Email", email],
                    ...(notes ? [["Note", notes]] : [])].map(([k, v]) => (
                    <div key={k} className="flex justify-between px-5 py-3.5 border-b last:border-0" style={{ borderColor: "#f2ecdd" }}>
                      <span className="text-sm font-semibold" style={{ color: C.MUTED }}>{k}</span>
                      <span className="text-sm font-bold text-right max-w-[60%]" style={{ color: C.INK }}>{v}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 rounded-2xl p-5" style={{ background: "#E3F1EB" }}>
                  <p className="text-sm mb-1" style={{ color: "#3f5b52" }}>Caparra indicativa: <span data-testid="recap-deposit" className="font-bold" style={{ color: C.GREEN }}>{euro(estimate)}</span></p>
                  <p className="text-xs" style={{ color: "#6b8a80" }}>L'importo definitivo verrà stabilito dal ristorante in base ai coperti. Dopo la conferma riceverai via email il link per pagare la caparra.</p>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex justify-between mt-6">
        <button data-testid="booking-back" onClick={() => (step === 0 ? nav("/") : setStep(step - 1))}
          className="px-6 py-3 rounded-full border-2 font-bold flex items-center gap-2 bg-white transition-colors hover:bg-black/5" style={{ borderColor: "#e2d8c3", color: "#4a453f" }}><ChevronLeft className="w-4 h-4" /> Indietro</button>
        {step < STEPS.length - 1 ? (
          <button data-testid="booking-next" disabled={!canNext()} onClick={() => setStep(step + 1)} className={btnPrimary} style={{ background: accent }}>Avanti <ChevronRight className="w-4 h-4" /></button>
        ) : (
          <button data-testid="booking-submit" disabled={sending} onClick={submitRequest} className={btnPrimary} style={{ background: accent }}><Send className="w-4 h-4" /> {sending ? "Invio…" : "Invia richiesta"}</button>
        )}
      </div>
    </div>
  );
}
